import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
} from '@mui/material';
import { HighlightOff } from '@mui/icons-material';
import { validateSearchName } from '../utils/savedSearchHelpers';
import { VISIBILITY_TYPES } from '../utils/savedSearchConstants';
import EnhancedTextField from '../../EnhancedTextField';
import EnhancedSelect from '../../EnhancedSelect';
import { DialogHeader } from '../../../theme';

interface SaveAsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, visibility: string) => Promise<void>;
  existingNames?: string[];
  allowShare?: boolean;
  title?: string;
  placeholder?: string;
}

const VISIBILITY_OPTIONS = [
  {
    value: VISIBILITY_TYPES.PRIVATE,
    label: 'Visible only to me',
    description: 'This search will be private and only accessible by you'
  },
  {
    value: VISIBILITY_TYPES.COMPANY,
    label: 'Visible to all users on my team',
    description: 'All users in your company can use this search'
  },
  {
    value: VISIBILITY_TYPES.PARTNERS,
    label: 'Visible to all users on my team and all trading partners',
    description: 'All users in your company and trading partners can use this search'
  }
];

const getVisibilityOption = (visibilityType: string) => {
  return VISIBILITY_OPTIONS.find(opt => opt.value === visibilityType) || null;
};

function SaveAsDialog({
  open,
  onClose,
  onSave,
  existingNames = [],
  allowShare = true,
  title = 'Save Search As',
  placeholder = 'Enter a name for the saved search'
}: SaveAsDialogProps) {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState(VISIBILITY_TYPES.PRIVATE);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens to ensure clean state each time
  useEffect(() => {
    if (open) {
      setName('');
      setVisibility(VISIBILITY_TYPES.PRIVATE);
      setError('');
      setSaving(false);
    }
  }, [open]);

  const handleSave = async () => {
    const nameError = validateSearchName(name, existingNames);
    if (nameError) {
      setError(nameError);
      return;
    }

    setSaving(true);
    await onSave(name, visibility || VISIBILITY_TYPES.PRIVATE);
    setSaving(false);

    setName('');
    setVisibility(VISIBILITY_TYPES.PRIVATE);
    setError('');
  };

  const handleClose = (..._args: any[]) => {
    if (!saving) {
      setName('');
      setVisibility(VISIBILITY_TYPES.PRIVATE);
      setError('');
      onClose();
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) {
      setError('');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (saving) return;
        }
        handleClose(event, reason);
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: "12px"
          }
        }
      }}>
      <DialogTitle style={{ paddingBottom: '8px' }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container style={{ flex: 0.5, flexDirection: "column" }}>
            <DialogHeader>
              {title}
            </DialogHeader>
          </Grid>

          <IconButton
            onClick={handleClose}
            disabled={saving}
            style={{ padding: '8px' }}
            size="large">
            <HighlightOff style={{ color: "red" }}/>
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '8px', paddingBottom: '16px' }}>
        <EnhancedTextField
          autoFocus
          placeholder={placeholder}
          value={name}
          onChange={handleNameChange}
          error={error}
          disabled={saving}
          style={{ marginBottom: allowShare ? '12px' : 0 }}
        />

        {allowShare && (
          <EnhancedSelect
            label="Visibility"
            value={getVisibilityOption(visibility)}
            onChange={(option) => {
              const newVisibility = option ? option.value : VISIBILITY_TYPES.PRIVATE;
              setVisibility(newVisibility);
            }}
            disabled={saving}
            options={VISIBILITY_OPTIONS}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select visibility"
          />
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px", paddingTop: "8px" }}>
        <Button disabled={saving} onClick={handleClose}>Cancel</Button>
        <Button color="primary" disabled={!name.trim() || saving} onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SaveAsDialog;
